const TAG_TOKEN = /^[a-z0-9][a-z0-9_-]*$/i
const TAG_QUERY_TOKEN = /(^|\s)#([a-z0-9][a-z0-9_-]*)(?=\s|$)/gi

/** A standalone #tag query is a tag filter, not fuzzy free-text search. */
export function exactTagSearchQuery(query: string): string | null {
  const match = query.trim().match(/^#([a-z0-9][a-z0-9_-]*)$/i)
  const tag = match?.[1]
  return tag ? tag.toLowerCase() : null
}

/** Returns the trailing hashtag fragment that should offer tag completions. */
export function tagSearchPrefix(query: string): string | null {
  const match = query.match(/(?:^|\s)#([a-z0-9_-]*)$/i)
  const prefix = match?.[1]
  if (prefix === undefined || (prefix && !TAG_TOKEN.test(prefix))) return null
  return prefix.toLowerCase()
}

/** Separates known #tag criteria from the free-text portion of a search query. */
export function parseTagSearchQuery(
  query: string,
  availableTags: readonly string[],
): { text: string; tags: string[] } {
  const knownTags = new Set(availableTags.map((tag) => tag.toLowerCase()))
  const tags: string[] = []
  const text = query.replace(TAG_QUERY_TOKEN, (token, leadingSpace: string, rawTag: string) => {
    const tag = rawTag.toLowerCase()
    if (!knownTags.has(tag)) return token
    if (!tags.includes(tag)) tags.push(tag)
    return leadingSpace
  }).replace(/\s+/g, ' ').trim()
  return { text, tags }
}

/** Completes only the active trailing hashtag and leaves a separator for the next criterion. */
export function completeTagSearchQuery(query: string, selectedTag: string): string {
  const tag = selectedTag.trim().toLowerCase()
  if (!TAG_TOKEN.test(tag)) return query
  const fragment = query.match(/(^|\s)#[a-z0-9_-]*$/i)
  if (!fragment || fragment.index === undefined) return query
  const precedingQuery = query.slice(0, fragment.index)
  const duplicatePattern = new RegExp(`(^|\\s)#${tag}(?=\\s|$)`, 'i')
  if (duplicatePattern.test(precedingQuery)) return `${precedingQuery.trimEnd()} `
  return `${precedingQuery}${fragment[1]}#${tag} `
}

/** Removes one exact hashtag criterion while retaining all other search input. */
export function removeTagFromSearchQuery(query: string, selectedTag: string): string {
  const tag = selectedTag.trim().toLowerCase()
  if (!TAG_TOKEN.test(tag)) return query
  const tagPattern = new RegExp(`(^|\\s)#${tag}(?=\\s|$)`, 'gi')
  return query.replace(tagPattern, ' ').replace(/\s+/g, ' ').trim()
}
